import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { useTheme } from '@hivebrite/design-system/Theme';

import type { EventsWrapperProps } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/EventsWrapper';
import type { ListLayoutsDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/types';
import type { Event } from 'hb-builder/components/BuilderComponents/dynamic/types/event';
import { goToModuleDetailPage } from 'hb-builder/utils/goToModuleDetailPage';
import Attendees from 'hb-react/shared/components/DesignSystem/Attendees';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import EventDate from 'hb-react/shared/components/EventDate';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import Info from 'hb-react/shared/icons/Info';

interface DetailsProps {
  dynamicAttributes?: ListLayoutsDynamicAttributes;
  event: Event;
  handleMouseEnterInTitle: (cardId: number) => void;
  handleMouseLeaveInTitle: () => void;
  hoverEffectImage: any;
}

const Details = ({
  dynamicAttributes,
  event,
  handleMouseEnterInTitle,
  handleMouseLeaveInTitle,
  hoverEffectImage,
  ids,
  isBackOffice,
  navigate,
}: Pick<EventsWrapperProps, 'navigate' | 'isBackOffice' | 'ids'> &
  DetailsProps) => {
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const {
    attending_users: attendingUsers,
    has_attendees: hasAttendees,
    id,
    locations,
    network_categories: tags,
    title,
    total_attendees: totalAttendees,
    total_external_attendees: totalExternalAttendees,
    webinar_url: webinarUrl,
    start_date_timestamp: startDateTimestamp,
    end_date_timestamp: endDateTimestamp,
    timezone,
  } = event;

  const {
    displayAttendees,
    displayDate,
    displayLocation,
    displayOnlineEvent,
    displayTags,
    displayTitle,
  } = dynamicAttributes?.fields || {};

  const theme = useTheme();

  const noCategories = !displayTags || tags.length === 0;

  return (
    <Box flexDirection="column">
      {displayTitle && (
        <WrapperTitle
          $hoverOnImage={hoverEffectImage.isHover}
          $isRightCard={hoverEffectImage.cardId === id}
          mb={noCategories ? 3 : 1}
          onMouseEnter={() => handleMouseEnterInTitle(id)}
          onMouseLeave={() => handleMouseLeaveInTitle()}
          onClick={() =>
            ids &&
            navigate &&
            goToModuleDetailPage({ event, ids, isBackOffice, navigate })
          }
        >
          <ClampedLinkWorkaround
            clamp={2}
            lineHeight=""
            margin={-2}
            padding={2}
            useNativeClamp
          >
            {title}
          </ClampedLinkWorkaround>
        </WrapperTitle>
      )}
      {!noCategories && (
        <Box mb={2}>
          <PillsList
            pills={tags}
            seeLessLabel={s__('Tags|See less')}
            seeMoreLabel={s__('Tags|See more')}
          />
        </Box>
      )}
      {displayDate && (
        <Box mb={2}>
          <Box>{__('Date')}: </Box>
          <Box ml={1}>
            <EventDate
              event={{
                start_date: startDateTimestamp,
                end_date: endDateTimestamp,
                timezone,
              }}
              as="span"
              timezoneConfiguration={timeZoneConfiguration}
            />
          </Box>
        </Box>
      )}
      {displayLocation && locations?.address && (
        <Box mb={2}>
          <Box>{__('Location')}:</Box>
          <Box ml={1}>
            <ClampedLinkWorkaround
              clamp={3}
              lineHeight=""
              margin={-2}
              padding={2}
              useNativeClamp
            >
              {locations.address}
            </ClampedLinkWorkaround>
          </Box>
        </Box>
      )}
      {displayOnlineEvent && webinarUrl && (
        <StyledBox>
          <Box>{__('Online event')}</Box>
          <Box pl={2}>
            <Info height={20} width={20} fill={theme.colors.primary} />
          </Box>
        </StyledBox>
      )}
      {displayAttendees && (attendingUsers?.length > 0 || hasAttendees) && (
        <Box>
          <Attendees
            attendees={attendingUsers}
            maxAvatars={6}
            eventId={id}
            eventType="network_events_event"
            imgSize={40}
            totalAttendees={totalAttendees}
            totalExternalAttendees={totalExternalAttendees}
            lazy={!!hasAttendees}
          />
        </Box>
      )}
    </Box>
  );
};

export default Details;

const WrapperTitle = styled(Box).attrs({
  font: {
    _: 'h3-semibold',
    l: 'h1-semibold',
    xl: 'h1-semibold',
  },
  maxWidth: '676px',
})<{ $hoverOnImage: boolean; $isRightCard: boolean }>`
  ${({ $hoverOnImage, $isRightCard }) =>
    $hoverOnImage &&
    $isRightCard &&
    css`
      * {
        text-decoration: underline;
        color: ${({ theme }) => theme.colors.link};
      }
    `}
  cursor: pointer;
  &:hover div {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.link} !important;
  }
`;

const StyledBox = styled(Box).attrs(() => ({
  mb: 3,
  height: 20,
  tabIndex: 0,
  alignItems: 'center',
}))``;
